/**
 * Sidebar conversation-action hook. Extracted verbatim (behavior-preserving)
 * from layout/Sidebar.tsx — bundles the conversation lifecycle handlers that
 * were inline in the orchestrator:
 *
 *   - `handleCreateConversation` (rate-limited new-chat creation)
 *   - `handleConversationClick`  (drag-mode guard + remembered/first-module nav)
 *   - `handleConversationLongPress` (enter drag mode on long press)
 *   - rename inline-edit trio: `revertConversationName`,
 *     `handleSaveConversationName`, `handleConversationNameKeyDown`
 *   - `handleArchiveConversation` / `handleArchiveAllConversations`
 *   - the unused-but-preserved bulk ops `handleDeleteConversationById`,
 *     `handleDeleteAllConversations`, `handleImportConversations` (dead on
 *     develop too — kept to avoid changing semantics; see note below)
 *
 * Hook-order safety: the ONLY React hook in this cluster is the single
 * `handleCreateConversation` `useCallback`; everything else is a plain function
 * (no memo identity contract). The `useCallback` dependency array is
 * byte-identical to the original
 * (`[onCreateConversationProp, allConversations, activeConversation,
 * createConversation, onCloseSidebar, showToast]`). Calling this hook once at a
 * fixed position keeps a stable render-to-render hook order. `handleCreateConversation`
 * must stay defined before the `useSidebarWindowEvents` call that consumes it —
 * which it does (this hook is invoked just after the new-chat-name imports are in
 * scope and before the window-events wiring).
 *
 * Deterministic value math (click-target module resolution, the new/imported
 * conversation object shapes) is delegated to the `conversationActions` kernels
 * (pinned by conversationActions.test.ts) so the flows stay byte-equivalent.
 *
 * NOTE on the three "delete/import" handlers: they have ZERO call sites in
 * Sidebar.tsx (and had none on develop — they were `noUnusedLocals` baseline
 * errors). They are preserved here unchanged and returned so the orchestrator's
 * surface is unchanged; the orchestrator no longer references them, exactly as
 * before (it merely defined them). Relocating them is behavior-preserving.
 */

import { useCallback } from 'react';
import type { useNavigate } from 'react-router-dom';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import type { Conversation } from '@/types/conversations';
import { isConversationPinned } from '@/types/conversations';
import { hapticMedium } from '@/lib/capacitor';
import { recordChatCreation, getRecentChatCount, pickNewChatName } from '@/lib/greetings';
import { buildConversationPath } from '@/lib/workspace-routing';
import {
  buildNewConversation,
  buildImportedConversation,
} from '../conversationActions';

interface UseConversationActionsArgs {
  // create
  onCreateConversationProp?: () => void;
  onCloseSidebar?: () => void;
  onConversationClick?: () => void;
  allConversations: Conversation[];
  activeConversation: Conversation | null;
  createConversation: (conversation: Conversation) => Promise<void> | void;
  deleteConversation: (conversationId: string) => Promise<void> | void;
  showToast: (message: string, type?: 'success' | 'info' | 'error') => void;
  // click / drag-mode
  isDragMode: boolean;
  setIsDragMode: (value: boolean) => void;
  renamingConversationId: string | null;
  setRenamingConversationId: (value: string | null) => void;
  isReadOnly: boolean;
  navigate: ReturnType<typeof useNavigate>;
  workspaceId: string;
  /** The module actually open in the pane right now (null in chat-only) — used to
   *  preserve the current view when switching conversations. */
  currentModuleId: string | null;
  // rename
  editingConversationNameRefs: React.MutableRefObject<{ [conversationId: string]: HTMLSpanElement | null }>;
  token: string | null | undefined;
  queryClient: QueryClient;
  // archive
  setActiveConversation: (conversation: Conversation | null) => void;
  userConversations: Conversation[];
  setIsChatsGearMenuOpen: (value: boolean) => void;
}

export function useConversationActions({
  onCreateConversationProp,
  onCloseSidebar,
  onConversationClick,
  allConversations,
  activeConversation,
  createConversation,
  deleteConversation,
  showToast,
  isDragMode,
  setIsDragMode,
  renamingConversationId,
  setRenamingConversationId,
  isReadOnly,
  navigate,
  workspaceId,
  currentModuleId,
  editingConversationNameRefs,
  token,
  queryClient,
  setActiveConversation,
  userConversations,
  setIsChatsGearMenuOpen,
}: UseConversationActionsArgs) {
  const handleCreateConversation = useCallback(() => {
    if (onCreateConversationProp) {
      onCreateConversationProp();
      onCloseSidebar?.();
      return;
    }
    // Rate limit: max 2 new chats in 30 seconds
    if (getRecentChatCount() >= 2) {
      showToast('Slow down — too many new chats. Try using your current one.', 'warning');
      onCloseSidebar?.();
      return;
    }
    recordChatCreation();

    const existingNames = new Set(allConversations.map((conversation) => (conversation.name || '').trim().toLowerCase()));
    const conversationName = pickNewChatName(existingNames);

    // Carry over existing modules so the module pane stays visible — only the chat clears.
    const existingModules = activeConversation?.modules ?? [];

    const newConversation: Conversation = buildNewConversation({
      conversationName,
      existingModules,
      now: Date.now(),
    });

    // createConversation will set the new conversation as active and navigate to it after API responds.
    createConversation(newConversation);
    onCloseSidebar?.();
  }, [onCreateConversationProp, allConversations, activeConversation, createConversation, onCloseSidebar, showToast]);

  const handleConversationClick = (conversation: Conversation) => {
    if (isDragMode) {
      // Exit drag mode when clicking a conversation
      setIsDragMode(false);
      return;
    }
    if (renamingConversationId !== conversation.id) {
      // Don't call setActiveConversation directly - let route-sync handle it to avoid double updates.

      // A chat switch is a pure content swap: preserve the CURRENT view instead
      // of opening the target conversation's remembered module. Encode it in the
      // URL so MainLayout's "default module" auto-open effect doesn't force a
      // module open — `view: 'chat'` (→ /c/<id>/chat) suppresses that effect for
      // chat-only, and the live open module id keeps module+chat on the same
      // module. (`currentModuleId` is already gated by the live pane state.)
      navigate(
        buildConversationPath(
          workspaceId,
          conversation.id,
          currentModuleId ? { moduleId: currentModuleId } : { view: 'chat' },
        ),
      );

      onConversationClick?.();
    }
  };

  // Handle long press to enter edit mode (like iOS app reordering)
  const handleConversationLongPress = (conversation: Conversation) => {
    // Don't trigger if already in drag mode or if renaming
    if (isDragMode || renamingConversationId === conversation.id || isReadOnly) {
      return;
    }

    // Trigger haptic feedback for mobile
    hapticMedium();

    // Enter drag mode
    setIsDragMode(true);
  };

  const revertConversationName = (conversation: Conversation) => {
    const ref = editingConversationNameRefs.current[conversation.id];
    if (ref) {
      ref.textContent = conversation.name;
    }
    setRenamingConversationId(null);
  };

  async function handleSaveConversationName(conversation: Conversation, el: HTMLElement) {
    const newName = el.textContent?.trim() || '';

    if (!newName || newName === conversation.name) {
      revertConversationName(conversation);
      return;
    }

    try {
      await api.workspaces.updateConversation(
        token ?? null,
        workspaceId,
        conversation.id,
        { title: newName },
      );

      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversation.id) });
      setRenamingConversationId(null);
    } catch (err: any) {
      console.error('[ConversationSidebar] Failed to update conversation name:', err);
      revertConversationName(conversation);
    }
  }

  function handleConversationNameKeyDown(e: React.KeyboardEvent<HTMLSpanElement>, conversation: Conversation) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveConversationName(conversation, e.currentTarget);
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      revertConversationName(conversation);
      e.currentTarget.blur();
    }
  }

  const handleArchiveConversation = (e: React.MouseEvent, conversation: Conversation) => {
    e.stopPropagation();
    if (!window.confirm(`Archive "${conversation.name}"?`)) return;
    void (async () => {
      try {
        await api.workspaces.updateConversation(token ?? null, workspaceId, conversation.id, {
          archived: true,
        });
        if (activeConversation?.id === conversation.id) {
          setActiveConversation(null);
        }
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversation.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists(), refetchType: 'inactive' });
      } catch (error) {
        console.error('[ConversationSidebar] Failed to archive conversation:', error);
      }
    })();
  };

  const handleArchiveAllConversations = async () => {
    setIsChatsGearMenuOpen(false);
    const nonPinned = userConversations.filter((c) => !isConversationPinned(c));
    if (nonPinned.length === 0) return;
    if (!window.confirm(`Archive all ${nonPinned.length} unpinned chats?`)) return;
    for (const conversation of nonPinned) {
      try {
        await api.workspaces.updateConversation(token ?? null, workspaceId, conversation.id, {
          archived: true,
        });
      } catch (err) {
        console.error('[Sidebar] Failed to archive conversation:', conversation.id, err);
      }
    }
    setActiveConversation(null);
    queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists(), refetchType: 'inactive' });
  };

  const handleDeleteConversationById = async (conversationId: string): Promise<void> => {
    await deleteConversation(conversationId);
  };

  const handleDeleteAllConversations = async (): Promise<void> => {
    // Delete all conversations one by one, waiting for each to complete.
    for (const conversation of allConversations) {
      await deleteConversation(conversation.id);
    }
  };

  const handleImportConversations = async (conversationsToImport: Conversation[]): Promise<void> => {
    // The import source is `JSON.parse(text) as ExportData` in SettingsModal —
    // no runtime validation, so a file exported from a pre-#2448 version of
    // the app can still carry snake_case `created_at`/`updated_at`/`archived_at`
    // even though the type no longer admits them. Spreading the parsed object
    // would silently propagate those fields into local state. Pick the known
    // camelCase fields explicitly instead.
    for (const conversation of conversationsToImport) {
      const newConversation: Conversation = buildImportedConversation(
        conversation,
        Date.now(),
        Math.random().toString(36).substr(2, 9),
      );
      createConversation(newConversation);
      // Small delay to avoid race conditions
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  };

  return {
    handleCreateConversation,
    handleConversationClick,
    handleConversationLongPress,
    revertConversationName,
    handleSaveConversationName,
    handleConversationNameKeyDown,
    handleArchiveConversation,
    handleArchiveAllConversations,
    handleDeleteConversationById,
    handleDeleteAllConversations,
    handleImportConversations,
  };
}
